import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useErrorManager } from 'InternalLibrary';
import axiosInstance from 'utils/axiosInstance';
import isLogged from 'utils/isLogged';

type Props = {
  children: ReactNode;
};

type ClientBlockedStatus = {
  blocked: boolean;
  blockedUntil: string | null;
};

export type ClientBlockedStatusState = ClientBlockedStatus | undefined | null;

type ClientBlockedStatusContextType = {
  clientBlockedStatus: ClientBlockedStatusState;
  fetchClientBlockedStatus: () => Promise<ClientBlockedStatus | void>;
};

const ClientBlockedStatusContext =
  createContext<ClientBlockedStatusContextType>(
    {} as ClientBlockedStatusContextType,
  );

const useClientBlockedStatus = (): ClientBlockedStatusContextType =>
  useContext(ClientBlockedStatusContext);

const ClientBlockedStatusProvider = ({ children }: Props): JSX.Element => {
  const [clientBlockedStatus, setClientBlockedStatus] =
    useState<ClientBlockedStatusState>();
  const { setError } = useErrorManager();

  const fetchClientBlockedStatus =
    useCallback(async (): Promise<ClientBlockedStatus | void> => {
      try {
        const { data: fetchedClientBlockedStatus } = await axiosInstance.get(
          '/client/blockedStatus',
        );
        setClientBlockedStatus(fetchedClientBlockedStatus);
        return fetchedClientBlockedStatus;
      } catch (e) {
        setClientBlockedStatus(null);
        setError();
        throw e;
      }
    }, [setError]);

  useEffect(() => {
    if (clientBlockedStatus === undefined && isLogged())
      fetchClientBlockedStatus();
  }, []);

  const clientBlockedStatusContextValue = useMemo(
    () => ({
      clientBlockedStatus,
      fetchClientBlockedStatus,
    }),
    [clientBlockedStatus, fetchClientBlockedStatus],
  );

  return (
    <ClientBlockedStatusContext.Provider
      value={clientBlockedStatusContextValue}
    >
      {children}
    </ClientBlockedStatusContext.Provider>
  );
};

export { useClientBlockedStatus as default, ClientBlockedStatusProvider };
